import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ArrowDown } from '@Components/icons/arrow-down';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extensions';
import { Editor, EditorProvider, JSONContent, useCurrentEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useDebounceValue } from 'usehooks-ts';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import { FieldTypes, StandardFormFieldDto, V2InputFields } from '@app/models/dtos/form';
import { cn } from '@app/shadcn/util/lib';
import { useBuilderFieldsValue, useFieldActions } from '@app/store/jotai/entity-hooks';
import { useBuilderActions } from '@app/store/jotai/field-selectors';
import { useFormState } from '@app/store/jotai/form';
import { useInsertTarget } from '@app/store/jotai/insert-target';
import { AnswerPipe } from '@app/utils/richTextEditorExtenstion/answer-pipe';
import { AnswerPipeSuggestion, PipeSuggestionItem, filterPipeSuggestionItems } from '@app/utils/richTextEditorExtenstion/answer-pipe-suggestion';
import { FontSize } from '@app/utils/richTextEditorExtenstion/font-size';
import { getHtmlFromJson } from '@app/utils/richTextEditorExtenstion/get-html-from-json';
import { legacyTitleText } from '@app/utils/legacy-title';
import { getGroupPipeOptions, isRepeatingGroup } from '@app/utils/repeating-groups';
import { buildSourceFields } from '@app/views/molecules/form-builder/condition-editor-shared';
import { HEADING_CLASSES, TEXT_BLOCK_BODY_CLASSES, TEXT_BLOCK_HEADING_LEVELS } from '@app/utils/text-headings';

export function getPlaceholderValueForTitle(fieldType: FieldTypes) {
    switch (fieldType) {
        case FieldTypes.EMAIL:
            return 'Enter Your Email Address';
        case FieldTypes.NUMBER:
            return 'Enter Number';
        case FieldTypes.SHORT_TEXT:
            return 'Enter Question';
        case FieldTypes.LINK:
            return 'Enter Link';
        case FieldTypes.PHONE_NUMBER:
            return 'Enter Your Phone Number';
        case FieldTypes.FILE_UPLOAD:
            return 'Upload Your File';
        case FieldTypes.YES_NO:
            return 'Are you sure?';
        case FieldTypes.DROP_DOWN:
            return 'Select an option';
        case FieldTypes.MULTIPLE_CHOICE:
            return 'Select from list below.';
        case FieldTypes.TEXT:
            return 'Add Text';
        case FieldTypes.RATING:
            return 'Rate from 1 to 5';
        case FieldTypes.DATE:
            return 'Select a date';
        case FieldTypes.DAY:
            return 'Enter a date';
        case FieldTypes.TIME:
            return 'Select a time';
        case FieldTypes.CURRENCY:
            return 'Enter an amount';
        case FieldTypes.DATE_RANGE:
            return 'Select the first and the last day';
        case FieldTypes.TAGS:
            return 'Type a tag and press Enter';
        case FieldTypes.ADDRESS:
            return 'Enter an address';
        case FieldTypes.SIGNATURE:
            return 'Sign here';
        case FieldTypes.LINEAR_RATING:
            return 'Rate from 1 to 10';
        case FieldTypes.MATRIX:
            return 'Matrix Field';
        case FieldTypes.TABULAR_INPUT:
            return 'Tabular Input Field';
        case FieldTypes.GROUP:
            return 'Repeating group';
        case FieldTypes.CALCULATED:
            return 'Calculation';
        default:
            return 'No Field Selected';
    }
}

// TipTap 3: underline ships inside StarterKit; link is switched off so titles
// keep the same stored JSON as before (no autolink marks).
export const Extenstions = [StarterKit.configure({ link: false }), TextStyle, FontSize, Color, AnswerPipe];

/**
 * What an empty title shows while it is being written. Only a visual hint
 * (a CSS placeholder): an untitled field stays untitled, nothing is saved.
 */
export function titlePlaceholder(fieldType?: FieldTypes | string | null) {
    return fieldType === FieldTypes.TEXT ? i18n.t('builder:RTE.PLACEHOLDER_TEXT') : i18n.t('builder:RTE.PLACEHOLDER_QUESTION');
}

/**
 * The editor's first content: the stored rich title, else a legacy plain
 * title (`value`), else nothing; an empty editor shows `titlePlaceholder`.
 */
export function initialTitleContent(field: Pick<StandardFormFieldDto, 'title' | 'value' | 'type' | 'properties'>): string {
    if (field.title) return getHtmlFromJson(field.title) ?? '';
    const legacy = legacyTitleText(field);
    return legacy ? (getHtmlFromJson(legacy) ?? '') : '';
}

/**
 * Everything pipeable into `field`'s title: answers the responder will already
 * have given (earlier pages, or earlier on this page — same scoping as
 * conditional logic) plus the form's declared hidden fields. Shared by the
 * "@ Answer" toolbar menu and the inline `@` suggestion picker.
 */
type PipeTarget = { field: Pick<StandardFormFieldDto, 'id' | 'index'>; slide: Pick<StandardFormFieldDto, 'index'> };

const answeredBefore = ({ field, slide }: PipeTarget, slideIndex: number, sourceField: StandardFormFieldDto) => slideIndex < slide.index || (slideIndex === slide.index && sourceField.index < field.index);

/** An earlier answer `target`'s title can pipe in (calculated values too, unless kept from respondents). */
function isPipeAnswer(target: PipeTarget, slideIndex: number, sourceField: StandardFormFieldDto): boolean {
    // calculated values pipe too, formatted as the field shows them
    // (not one kept from respondents: piping would show it to them)
    // an address is never piped into a title (answer-piping.ts)
    if (sourceField.type === FieldTypes.ADDRESS) return false;
    // a signature is never piped into a title: not the typed name, nothing about the drawing
    if (sourceField.type === FieldTypes.SIGNATURE) return false;
    if (!V2InputFields.includes(sourceField.type) && !(sourceField.type === FieldTypes.CALCULATED && !sourceField.properties?.hiddenFromRespondents)) return false;
    if (sourceField.id === target.field.id) return false;
    return answeredBefore(target, slideIndex, sourceField);
}

/** An earlier repeating group `target`'s title can pipe in, as a whole. */
const isPipeGroup = (target: PipeTarget, slideIndex: number, sourceField: StandardFormFieldDto) => isRepeatingGroup(sourceField) && sourceField.id !== target.field.id && answeredBefore(target, slideIndex, sourceField);

/** Is there anything to pipe into `target`'s title? (buildPipeItems would not be empty; no labels made.) */
function hasPipeItems(formFields: StandardFormFieldDto[], hiddenNames: string[], target: PipeTarget): boolean {
    if (hiddenNames.length) return true;
    return (formFields || []).some((sourceSlide, slideIndex) => (sourceSlide?.properties?.fields ?? []).some((sourceField) => !sourceField?.internal && (isPipeAnswer(target, slideIndex, sourceField) || isPipeGroup(target, slideIndex, sourceField))));
}

export function buildPipeItems(formFields: StandardFormFieldDto[], hiddenNames: string[], field: StandardFormFieldDto, slide: StandardFormFieldDto): PipeSuggestionItem[] {
    const target = { field, slide };
    const isAnswer = (sourceSlide: StandardFormFieldDto, slideIndex: number, sourceField: StandardFormFieldDto) => isPipeAnswer(target, slideIndex, sourceField);
    const isGroup = (sourceSlide: StandardFormFieldDto, slideIndex: number, sourceField: StandardFormFieldDto) => isPipeGroup(target, slideIndex, sourceField);
    // The menu shows labels in the builder's language; the chip stores the same label in English (it is part of the question's title, which respondents' pages read back).
    const sources = buildSourceFields(formFields, isAnswer);
    const storedSources = buildSourceFields(formFields, isAnswer, 'en');
    // Earlier repeating groups pipe as a whole: item count, lists, aggregates.
    const groups = buildSourceFields(formFields, isGroup);
    const storedGroups = buildSourceFields(formFields, isGroup, 'en');
    return [
        ...sources.map(({ field: sourceField, label }, i): PipeSuggestionItem => ({ kind: 'field', pipeKey: sourceField.id, label, storedLabel: storedSources[i]?.label ?? label, group: 'Answers' })),
        ...groups.flatMap(({ field: group, label }, i) => {
            const stored = getGroupPipeOptions(group, storedGroups[i]?.label ?? label);
            return getGroupPipeOptions(group, label).map((option, j): PipeSuggestionItem => ({ kind: 'group', ...option, storedLabel: stored[j]?.label ?? option.label, group: 'Repeating groups' }));
        }),
        ...hiddenNames.map((name): PipeSuggestionItem => ({ kind: 'hidden', pipeKey: name, label: name, group: 'Hidden fields' }))
    ];
}

/**
 * Whether the editor must take the stored title: it differs from what the
 * editor shows. A string (legacy) title has no JSON form to compare; it is
 * synced only when it changed, which the caller checks.
 */
export function titleNeedsSync(stored: JSONContent | string | undefined | null, shown: JSONContent): boolean {
    if (!stored || typeof stored === 'string') return true;
    return JSON.stringify(stored) !== JSON.stringify(shown);
}

function usePreviousState(value: any) {
    const ref = useRef(value);
    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current;
}

export function RichTextEditor({
    field,
    slide,
    autofocus = false,
    isRequired = false,
    describedById
}: {
    field: StandardFormFieldDto;
    slide: StandardFormFieldDto;
    autofocus?: boolean;
    isRequired?: boolean;
    /** Id of helper text for the title (plain-language tips), for aria-describedby. */
    describedById?: string;
}) {
    const { updateTitle } = useFieldActions();
    // The form is read when the `@` suggestion asks for it, not subscribed
    // to: the editor re-renders only when its own question changes.
    const { getFormFields } = useBuilderActions();
    const { formState } = useFormState();

    // The `@` suggestion needs the CURRENT builder state on every keystroke,
    // but the extension list must keep a stable identity (a new array would
    // recreate the editor per render). A ref bridges the two.
    const pipeContextRef = useRef({ getFormFields, hiddenNames: formState.hiddenFields ?? [], field, slide });
    pipeContextRef.current = { getFormFields, hiddenNames: formState.hiddenFields ?? [], field, slide };

    const editorExtensions = useMemo(
        () => [
            ...Extenstions,
            Placeholder.configure({ placeholder: () => titlePlaceholder(pipeContextRef.current.field.type) }),
            AnswerPipeSuggestion.configure({
                getItems: (query: string) => {
                    const { getFormFields: formFields, hiddenNames, field: currentField, slide: currentSlide } = pipeContextRef.current;
                    return filterPipeSuggestionItems(buildPipeItems(formFields(), hiddenNames, currentField, currentSlide), query);
                }
            })
        ],
         
        []
    );

    const getContentForEditor = () => initialTitleContent(field);
    const { setInsertTarget } = useInsertTarget();
    const editorId = `title-editor-${field.id}`;

    // "/" in an empty title opens the insert menu here: an empty text block
    // becomes the picked field, a question gets it right after.
    const openInsertMenu = () => {
        const isTextBlock = field.type === FieldTypes.TEXT;
        setInsertTarget({
            slideId: slide.id,
            slideIndex: slide.index,
            fieldIndex: isTextBlock ? field.index : field.index + 1,
            replaceFieldId: isTextBlock ? field.id : undefined,
            anchorId: editorId,
            returnFocusId: editorId
        });
    };
    // The editor keeps the key handler it was created with; the ref gives it
    // the field's current position.
    const openInsertMenuRef = useRef(openInsertMenu);
    useEffect(() => {
        openInsertMenuRef.current = openInsertMenu;
    });

    // `generation` tells a typed value apart from text replaced from outside
    // since: a pending write of replaced text must not be saved back.
    const [jsonVal, setJSONVal] = useState<{ json: JSONContent; generation: number } | null>(null);
    const generationRef = useRef(0);
    const editorRef = useRef<Editor | null>(null);
    // The stored title as this editor last wrote or showed it.
    const storedTitle = JSON.stringify(field.title ?? null);
    const lastSyncedTitleRef = useRef(storedTitle);

    const [isBold, setIsBold] = useState(true);
    const previousState = usePreviousState(isBold);

    const [debouncedInputValue] = useDebounceValue(jsonVal, 300);
    // What was typed and is not written yet (the debounce still holds it).
    const pendingRef = useRef<{ json: JSONContent; generation: number } | null>(null);

    useEffect(() => {
        if (pendingRef.current === debouncedInputValue) pendingRef.current = null;
        if (debouncedInputValue && debouncedInputValue.generation === generationRef.current) {
            lastSyncedTitleRef.current = JSON.stringify(debouncedInputValue.json);
            updateTitle(field.id, debouncedInputValue.json);
        }
    }, [debouncedInputValue]);

    // Unmounting (leaving the page or the editor) writes what the debounce
    // still holds, unless text replaced from outside since makes it stale.
    // A layout-effect cleanup: it runs before the editor page's own cleanup
    // lets go of the form, so autosave still takes it as an edit.
    const fieldId = field.id;
    useLayoutEffect(
        () => () => {
            const pending = pendingRef.current;
            pendingRef.current = null;
            if (pending && pending.generation === generationRef.current && JSON.stringify(pending.json) !== lastSyncedTitleRef.current) updateTitle(fieldId, pending.json);
        },
        [fieldId, updateTitle]
    );

    // The editor reads `content` only when it mounts. When the stored title
    // changes from outside (undo, redo, an AI edit), show it, without
    // emitting an update, so stale text is never written back.
    useEffect(() => {
        const editor = editorRef.current;
        if (!editor || editor.isDestroyed || storedTitle === lastSyncedTitleRef.current) return;
        lastSyncedTitleRef.current = storedTitle;
        if (!titleNeedsSync(field.title, editor.getJSON())) return;
        generationRef.current += 1;
        editor.commands.setContent(field.title && typeof field.title !== 'string' ? field.title : getContentForEditor(), { emitUpdate: false });
    }, [storedTitle]);

    return (
        <div
            className={cn(
                'tiptap group relative flex w-full',
                // The placeholder is a hint, so it reads quieter than a title.
                '[&_.is-editor-empty]:before:font-normal [&_.is-editor-empty]:before:opacity-50',
                // Required: the star follows the title's last word, as respondents
                // see it, instead of sitting at the far end of the line. A CSS
                // marker, so it is never part of the editable title text.
                isRequired && "[&_.ProseMirror>p:last-child]:after:ml-1.5 [&_.ProseMirror>p:last-child]:after:text-[#C43D3D] [&_.ProseMirror>p:last-child]:after:content-['*']"
            )}
            data-required={isRequired ? 'true' : undefined}
        >
            <EditorProvider
                content={getContentForEditor()}
                extensions={editorExtensions}
                immediatelyRender={false}
                // TipTap 3 stops re-rendering on every transaction by default; the
                // menu bar reads editor.isActive() so it needs the v2 behaviour.
                shouldRerenderOnTransaction
                slotBefore={<TiptapMenuBar field={field} slide={slide} />}
                autofocus={autofocus}
                editorProps={{
                    attributes: {
                        // Match the responder's question scale (24px/600) so the
                        // canvas is honest about what responders will see.
                        // A text block is content, so it shows as body text, as respondents see it.
                        class: `outline-hidden w-full max-w-full min-w-[300px] ${field.type === FieldTypes.TEXT ? TEXT_BLOCK_BODY_CLASSES : 'text-2xl font-semibold leading-snug'} ${HEADING_CLASSES}`,
                        style: 'word-break: break-word',
                        id: editorId,
                        role: 'textbox',
                        'aria-multiline': 'true',
                        'aria-label': field.type === FieldTypes.TEXT ? i18n.t('builder:RTE.ARIA_TEXT_BLOCK') : i18n.t('builder:RTE.ARIA_QUESTION_TITLE'),
                        ...(describedById ? { 'aria-describedby': describedById } : {})
                    },
                    handleKeyDown: (_view, event) => {
                        const editor = editorRef.current;
                        if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey || !editor?.isEmpty) return false;
                        event.preventDefault();
                        openInsertMenuRef.current();
                        return true;
                    }
                }}
                onFocus={({ editor }) => {
                    // Question titles are typed bold, as they always were.
                    if (editor.isEmpty && field.type !== FieldTypes.TEXT && isBold) editor.commands.setBold();
                }}
                onCreate={({ editor }) => {
                    editorRef.current = editor;
                }}
                onDestroy={() => {
                    editorRef.current = null;
                }}
                onUpdate={({ editor }) => {
                    const typed = { json: editor.getJSON(), generation: generationRef.current };
                    pendingRef.current = typed;
                    setJSONVal(typed);
                    setIsBold(editor?.isActive('bold'));
                    if (editor.getText() === '') {
                        previousState ? editor?.chain().focus().setBold().run() : editor?.chain().focus().unsetBold().run();
                    }
                }}
            >
                {''}
            </EditorProvider>
        </div>
    );
}

const getActiveFontSize = (editor?: Editor) => {
    if (!editor) return null;
    const fontSize = editor.getAttributes('textStyle')?.fontSize || '';
    const fontSizeInNum = Number((fontSize.match(/\d+/g) || []).join(''));
    return fontSizeInNum;
};

// "Insert answer" dropdown: pipes an earlier question's answer (or a hidden
// URL parameter) into this title at the cursor, as an answerPipe chip. The
// inline `@` suggestion (AnswerPipeSuggestion) is the fast path; this button
// is the discoverable one.
const InsertPipeMenu = ({ editor, field, slide }: { editor: Editor; field: StandardFormFieldDto; slide: StandardFormFieldDto }) => {
    const { t } = useTranslation('builder');
    const { getFormFields } = useBuilderActions();
    const { formState } = useFormState();
    const [open, setOpen] = useState(false);
    // The items are listed when the menu opens; until then only whether
    // there are any is followed (a derived value, cheap: no labels made).
    const [items, setItems] = useState<PipeSuggestionItem[]>([]);
    const menuId = `pipe-menu-${field.id}`;
    const hiddenNames = formState.hiddenFields;
    const fieldId = field.id;
    const fieldIndex = field.index;
    const slideIndex = slide.index;
    const available = useBuilderFieldsValue((formFields) => hasPipeItems(formFields, hiddenNames ?? [], { field: { id: fieldId, index: fieldIndex }, slide: { index: slideIndex } }), [hiddenNames, fieldId, fieldIndex, slideIndex]);
    if (!available) return null;

    const toggleMenu = () => {
        if (!open) setItems(buildPipeItems(getFormFields(), hiddenNames ?? [], field, slide));
        setOpen(!open);
    };

    const insertPipe = (item: PipeSuggestionItem) => {
        editor
            .chain()
            .focus()
            .insertContent([
                { type: 'answerPipe', attrs: { kind: item.kind, pipeKey: item.pipeKey, label: item.storedLabel ?? item.label } },
                { type: 'text', text: ' ' }
            ])
            .run();
        setOpen(false);
    };

    let lastGroup: string | null = null;

    return (
        <div className="relative">
            {/* mousedown is swallowed so the editor keeps focus (the floating bar is focus-within-gated). */}
            <button
                type="button"
                aria-haspopup="true"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                title={t('RTE.PIPE_TITLE')}
                className="cursor-pointer whitespace-nowrap rounded px-1 text-sm font-medium text-blue-600 hover:bg-gray-100"
                onMouseDown={(e) => e.preventDefault()}
                onClick={toggleMenu}
                onKeyDown={(e) => {
                    if (e.key === 'Escape' && open) {
                        e.stopPropagation();
                        setOpen(false);
                    }
                }}
            >
                {t('RTE.PIPE_BUTTON')}
            </button>
            {open && (
                <div id={menuId} role="group" aria-label={t('RTE.PIPE_MENU')} className="absolute left-0 top-full z-50 mt-1 max-h-64 w-72 overflow-auto rounded border border-black-300 bg-white py-1 text-left shadow-large">
                    {items.map((item) => {
                        const header = item.group !== lastGroup ? t(`RTE.GROUP_${item.group}`) : null;
                        lastGroup = item.group;
                        return (
                            <React.Fragment key={`${item.kind}:${item.pipeKey}`}>
                                {header && <div className="px-3 py-1 text-xs uppercase tracking-wide text-black-600">{header}</div>}
                                <button type="button" className="block w-full cursor-pointer truncate px-3 py-1.5 text-left text-sm text-black-800 hover:bg-gray-100 focus-visible:bg-gray-100" onMouseDown={(e) => e.preventDefault()} onClick={() => insertPipe(item)}>
                                    {item.label}
                                </button>
                            </React.Fragment>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/** One toolbar button: a real button that keeps the editor's selection. */
const ToolbarButton = ({ label, pressed, onClick, children, className }: { label: string; pressed?: boolean; onClick: () => void; children: React.ReactNode; className?: string }) => (
    <button
        type="button"
        aria-label={label}
        title={label}
        aria-pressed={pressed}
        // Keep the editor's selection: the click formats it.
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        className={cn('flex cursor-pointer items-center justify-center rounded hover:bg-gray-100', pressed && 'bg-gray-200', className)}
    >
        {children}
    </button>
);

// Heading levels for text blocks only: a question's title is its label, so it
// stays a paragraph. Clicking the active level turns the line back into text.
const HeadingButtons = ({ editor }: { editor: Editor }) => {
    const { t } = useTranslation('builder');
    return (
        <div className="flex items-center gap-1 border-l border-black-300 pl-3" role="group" aria-label={t('RTE.HEADING_LEVEL')}>
            {TEXT_BLOCK_HEADING_LEVELS.map((level) => (
                <ToolbarButton key={level} label={t('RTE.HEADING_N', { level })} pressed={editor.isActive('heading', { level })} onClick={() => editor.chain().focus().toggleHeading({ level }).run()} className="px-1 text-sm font-semibold text-black-700">
                    <span aria-hidden="true">H{level}</span>
                </ToolbarButton>
            ))}
        </div>
    );
};

const TiptapMenuBar = ({ field, slide }: { field: StandardFormFieldDto; slide: StandardFormFieldDto }) => {
    const { t } = useTranslation('builder');
    const editorRef = useCurrentEditor();
    // Constrain label sizing to the type scale (DESIGN.md §2) so creators
    // stay on a coherent hierarchy: Meta 12 · Helper/Consent 15 · Body/Input 16 ·
    // Question 24 · Display 32. No off-scale steps that break the reading rhythm.
    const FontSizes = [12, 15, 16, 24, 32];
    const editor = editorRef.editor;
    if (!editor) {
        return null;
    }

    const activeSize = getActiveFontSize(editor) || 24;
    const handleUpdateFontSize = (value = 0) => {
        const currentActiveIndex = FontSizes.findIndex((num) => num == activeSize);
        // Clamp to the ends of the scale so stepping never lands on undefined.
        const newSize = Math.min(Math.max(currentActiveIndex + value, 0), FontSizes.length - 1);
        editor?.chain().focus().setFontSize(`${FontSizes[newSize]}`).run();
    };

    return (
        // Shown while the title (or this bar) has focus.
        <div role="toolbar" aria-label={t('RTE.FORMATTING')} className={cn(`absolute -top-14 mb-2 hidden items-center rounded border border-black-300 bg-white px-4 py-1 shadow-large`, 'group-focus-within:flex')}>
            <div className="flex flex-row items-center justify-center gap-4">
                <span className="p3-new font-medium" aria-hidden="true">
                    {t('RTE.TEXT_LABEL')}
                </span>
                <div className="flex items-center gap-1" role="group" aria-label={t('RTE.TEXT_SIZE', { size: activeSize })}>
                    <span className="p3-new w-[21px] text-black-700" aria-hidden="true">
                        {activeSize}
                    </span>
                    <div className="flex flex-col">
                        <ToolbarButton label={t('RTE.LARGER')} onClick={() => handleUpdateFontSize(+1)}>
                            <ArrowDown className="h-4 w-4 rotate-180" aria-hidden="true" />
                        </ToolbarButton>
                        <ToolbarButton label={t('RTE.SMALLER')} onClick={() => handleUpdateFontSize(-1)}>
                            <ArrowDown className="h-4 w-4" aria-hidden="true" />
                        </ToolbarButton>
                    </div>
                </div>
                <ToolbarButton label={t('RTE.BOLD')} pressed={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M6.51811 19V5.18182H11.5785C12.5591 5.18182 13.371 5.34375 14.0142 5.66761C14.6574 5.98698 15.1387 6.42105 15.4581 6.96982C15.7775 7.51409 15.9371 8.12808 15.9371 8.81179C15.9371 9.38755 15.8314 9.87334 15.62 10.2692C15.4086 10.6605 15.1252 10.9754 14.7699 11.2138C14.419 11.4477 14.0322 11.6186 13.6094 11.7266V11.8615C14.0682 11.884 14.5157 12.0324 14.9521 12.3068C15.3929 12.5767 15.7572 12.9613 16.0451 13.4606C16.333 13.9599 16.4769 14.5671 16.4769 15.2823C16.4769 15.9885 16.3105 16.6228 15.9776 17.185C15.6493 17.7428 15.141 18.1858 14.4528 18.5142C13.7646 18.8381 12.8852 19 11.8146 19H6.51811ZM8.60298 17.212H11.6122C12.6108 17.212 13.326 17.0186 13.7578 16.6317C14.1896 16.2449 14.4055 15.7614 14.4055 15.1811C14.4055 14.7448 14.2953 14.3445 14.0749 13.9801C13.8545 13.6158 13.5397 13.3256 13.1303 13.1097C12.7255 12.8938 12.2442 12.7859 11.6864 12.7859H8.60298V17.212ZM8.60298 11.1598H11.3963C11.8641 11.1598 12.2847 11.0698 12.658 10.8899C13.0359 10.71 13.335 10.4581 13.5554 10.1342C13.7803 9.80587 13.8928 9.41903 13.8928 8.97372C13.8928 8.40246 13.6926 7.92341 13.2923 7.53658C12.8919 7.14974 12.2779 6.95632 11.4503 6.95632H8.60298V11.1598Z" fill="#4D4D4D" />
                    </svg>
                </ToolbarButton>
                <ToolbarButton label={t('RTE.ITALIC')} pressed={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M11.3 5.36C11.3267 5.34667 11.44 5.34 11.64 5.34C13.1733 5.40667 14.8 5.41333 16.52 5.36C16.9333 5.34667 17.1667 5.38 17.22 5.46C17.2467 5.5 17.2333 5.65333 17.18 5.92C17.1133 6.22667 17.0467 6.39333 16.98 6.42C16.9133 6.44667 16.68 6.46 16.28 6.46C15.6533 6.47333 15.2867 6.50667 15.18 6.56C15.1133 6.58667 15.0667 6.63333 15.04 6.7C14.9733 6.88667 14.5 8.72667 13.62 12.22C12.7 15.9133 12.2467 17.7667 12.26 17.78C12.2867 17.8467 12.7133 17.88 13.54 17.88H13.98L14.06 17.94C14.1 17.9933 14.12 18.04 14.12 18.08L13.94 18.88C13.9 18.96 13.76 18.9933 13.52 18.98C13.4533 18.98 13.3267 18.98 13.14 18.98C11.06 18.9267 9.43333 18.9333 8.26 19H8.02L7.96 18.94C7.90667 18.8867 7.88 18.84 7.88 18.8L8.06 18C8.1 17.92 8.28667 17.88 8.62 17.88C8.68667 17.88 8.76667 17.88 8.86 17.88C9.56667 17.8667 9.97333 17.8133 10.08 17.72C10.0933 17.7067 10.5667 15.8533 11.5 12.16C12.42 8.48 12.88 6.62667 12.88 6.6C12.88 6.58667 12.8667 6.56667 12.84 6.54C12.7733 6.5 12.4267 6.47333 11.8 6.46H11.16L11.08 6.38C11.0133 6.31333 11.02 6.13333 11.1 5.84C11.18 5.56 11.2467 5.4 11.3 5.36Z" fill="#4D4D4D" />
                    </svg>
                </ToolbarButton>
                <ToolbarButton label={t('RTE.UNDERLINE')} pressed={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
                    <u className="px-1 text-xl" aria-hidden="true">
                        U
                    </u>
                </ToolbarButton>
                {field.type === FieldTypes.TEXT && <HeadingButtons editor={editor} />}
                <InsertPipeMenu editor={editor} field={field} slide={slide} />
            </div>
        </div>
    );
};
